import { ColumnType, CustomSwitch } from "@/components";
import Popover from "@/components/Popover";
import { useAuth } from "@/contexts/AuthContext";
import { useDebounce } from "@/hooks/useDebounce";
import { useModalStore } from "@/store/modalStore";
import { useUserStore } from "@/store/userStore";
import {
  PriorityRole,
  RoleEnum,
  RoleManagerExcute,
  UserInStore,
} from "@/types";
import {
  cn,
  DEFAULT_PAGE_SIZE,
  KEYS_SEARCH_PARAMS,
  ROLE_LABELS,
} from "@/utils";
import { useEffect, useRef, useState } from "react";
import { HiDotsVertical } from "react-icons/hi";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { DeleteUser } from "../components/DeleteUser";
import { TransferUserModal } from "../components/TransferUserModal";
import { DELETE_USER_IN_STORE_MODAL_NAME } from "./useDeleteUser";

const defaultClassPopverTrigger =
  "user-menu-item px-4 py-2 cursor-pointer whitespace-nowrap hover:bg-gray-100 border-b border-gray-200";

const useUsersInStore = ({
  storeStatus,
}: {
  storeStatus?: "ENABLE" | "DISABLE";
}) => {
  const { id } = useParams();
  const [currentUser, setCurrentUser] = useState<number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const params = Object.fromEntries([...searchParams]);
  const currentPage = Number(params[KEYS_SEARCH_PARAMS.PAGE]) || 1;

  const { role } = useAuth();

  const navigate = useNavigate();

  const isDisabled = storeStatus === "DISABLE";

  const isInitialMount = useRef(true);

  const [inputValue, setInputValue] = useState<string>(
    params[KEYS_SEARCH_PARAMS.SEARCH] || ""
  );

  const debouncedSearchTerm = useDebounce(inputValue?.trim(), 500);

  const {
    fetchUsersInStore,
    usersInStore,
    loading,
    updateUserStatus,
    fetchMyUsersStore,
  } = useUserStore();

  const { modals } = useModalStore();

  const isOpenDeleteModal = modals[DELETE_USER_IN_STORE_MODAL_NAME];

  const updateSearchParams = (object: { [key: string]: string }) => {
    const inputParams: { [key: string]: string } = params;
    for (const key in object) {
      const value = object[key]?.trim();
      if (value) {
        inputParams[key] = value;
      } else {
        delete inputParams[key];
      }
    }
    setSearchParams(inputParams, { replace: true });
  };

  const handleUsersKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      updateSearchParams({ [KEYS_SEARCH_PARAMS.SEARCH]: inputValue });
    }
  };

  const isUserLearningViewDisabled = (userId?: number, userRole?: RoleEnum) => {
    return (
      !userId ||
      !RoleManagerExcute.USER_LEARNING.VIEW[role || RoleEnum.OTHER] ||
      PriorityRole[role || RoleEnum.OTHER] <
        PriorityRole[userRole || RoleEnum.OTHER]
    );
  };
  const isUserInfoViewDisabled = (userId?: number, userRole?: RoleEnum) => {
    return (
      !userId ||
      !RoleManagerExcute.USER_INFORMATION.VIEW[role || RoleEnum.OTHER] ||
      PriorityRole[role || RoleEnum.OTHER] <
        PriorityRole[userRole || RoleEnum.OTHER]
    );
  };

  // Users Table Columns
  const usersColumns: ColumnType<UserInStore>[] = [
    {
      title: "氏名",
      dataIndex: "full_name",
      key: "full_name",
      width: "26%",
    },
    {
      title: "メールアドレス",
      dataIndex: "email",
      key: "email",
      width: "26%",
      render: (email) => <p className="break-all">{email}</p>,
    },
    {
      title: "役割",
      dataIndex: "role",
      key: "role",
      width: "17%",
      render: (_, record) => <p>{ROLE_LABELS[record.role]}</p>,
    },
    {
      title: "学習",
      dataIndex: "lesson_completed",
      key: "lesson_completed",
      width: "10%",
    },
    {
      title: "テスト",
      dataIndex: "quiz_completed",
      key: "quiz_completed",
      width: "10%",
    },
    {
      title: " ",
      key: "actions",
      width: "11%",
      render: (_, record) => (
        <div className="flex flex-row items-center justify-between w-full gap-2">
          {RoleManagerExcute.USER_IN_STORE.ACTIVE[role || RoleEnum.OTHER] && (
            <CustomSwitch
              checked={isDisabled ? false : record.status === "ENABLE"}
              onCheckedChange={() => updateUserStatus(record.id)}
              size="xs"
              className="px-[2px] py-[7px]"
              disabled={
                isDisabled ||
                PriorityRole[role || RoleEnum.OTHER] <=
                  PriorityRole[record.role as RoleEnum]
              }
            />
          )}
          {/* Popover for user actions */}
          <div className="relative">
            <Popover
              trigger={
                <div className="flex items-center justify-center size-6">
                  <HiDotsVertical
                    className={cn(
                      "size-4 ",
                      isDisabled
                        ? "cursor-not-allowed text-gray-400"
                        : "hover:text-cyan"
                    )}
                  />
                </div>
              }
              side="bottom"
              align="end"
            >
              <div
                className={cn(
                  "user-menu-dropdown bg-white shadow-lg rounded-lg border border-gray-200 min-w-[160px] text-sm",
                  isOpenDeleteModal || isDisabled ? "hidden" : ""
                )}
              >
                <div
                  className={cn(
                    defaultClassPopverTrigger,
                    isUserLearningViewDisabled(record.id, record.role)
                      ? "opacity-50"
                      : ""
                  )}
                  onClick={() => {
                    if (isUserInfoViewDisabled(record.id, record.role)) return;
                    navigate(`/user-information/${record.id}`);
                  }}
                >
                  ユーザー情報
                </div>
                {RoleManagerExcute.USER_IN_STORE.MOVING[
                  role || RoleEnum.OTHER
                ] && (
                  <div
                    className={cn(defaultClassPopverTrigger)}
                    onClick={() => {
                      setCurrentUser(record.id);
                    }}
                  >
                    人事異動
                  </div>
                )}
                <div
                  className={cn(
                    defaultClassPopverTrigger,
                    isUserLearningViewDisabled(record.id, record.role)
                      ? "opacity-50"
                      : ""
                  )}
                  onClick={() => {
                    if (isUserLearningViewDisabled(record.id, record.role)) {
                      return;
                    }
                    navigate(`/user-information/${record.id}/learning`);
                  }}
                >
                  学習詳細
                </div>
                <DeleteUser
                  triggerClassName="user-menu-item px-4 py-2 cursor-pointer whitespace-nowrap text-red-500 hover:bg-red-50"
                  userId={record.id}
                  disabled={role === record?.role}
                >
                  アカウントを削除
                </DeleteUser>
              </div>
            </Popover>
          </div>

          <TransferUserModal
            key={record.id.toString()}
            open={currentUser === record.id}
            setOpen={(open) => setCurrentUser(open ? record.id : null)}
            handleClose={() => setCurrentUser(null)}
            userName={record.full_name}
            userId={record.id}
            defaultStore={Number(id)}
            className={defaultClassPopverTrigger}
            onRefresh={onFetchStore}
          />
        </div>
      ),
    },
  ];

  const onFetchStore = () => {
    if (!id) return;
    const functionFetch =
      role === RoleEnum.STORE_MANAGER ? fetchMyUsersStore : fetchUsersInStore;
    functionFetch({
      id,
      page: currentPage,
      s: debouncedSearchTerm,
      limit: DEFAULT_PAGE_SIZE,
    });
  };

  useEffect(() => {
    onFetchStore();
  }, [id, currentPage, debouncedSearchTerm]);

  useEffect(() => {
    // Skip updating search params on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (debouncedSearchTerm) {
      updateSearchParams({
        [KEYS_SEARCH_PARAMS.SEARCH]: debouncedSearchTerm,
        [KEYS_SEARCH_PARAMS.PAGE]: "1",
      });
    } else {
      updateSearchParams({ [KEYS_SEARCH_PARAMS.SEARCH]: "" });
    }
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (!!usersInStore.data.length) return;

    updateSearchParams({
      [KEYS_SEARCH_PARAMS.PAGE]: String(currentPage - 1 || 1),
    });
  }, [usersInStore.data.length]);

  return {
    inputValue,
    setInputValue,
    handleUsersKeyDown,
    id,
    usersColumns,
    currentPage,
    updateSearchParams,
    usersInStore,
    loading,
    isDisabled,
  };
};

export { KEYS_SEARCH_PARAMS, useUsersInStore };
